import React, { useState } from 'react';
import { auth } from '../../config/firebase';
import {
  confirmPasswordReset,
  verifyPasswordResetCode
} from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../../logo.png';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const oobCode = searchParams.get('oobCode');

  async function handleReset(e) {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters!');
      return;
    }
    if (!password.match(/[A-Z]/)) {
      setError('Password must have at least one capital letter!');
      return;
    }
    if (!password.match(/[0-9]/)) {
      setError('Password must have at least one number!');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match!');
      return;
    }
    if (!oobCode) {
      setError(
        'Invalid reset link. Please request a new one.'
      );
      return;
    }

    setLoading(true);
    try {
      await verifyPasswordResetCode(auth, oobCode);
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(
        'Password reset successfully! Redirecting to login...'
      );
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      if (err.code === 'auth/expired-action-code') {
        setError(
          'This reset link has expired. Please request a new one.'
        );
      } else if (err.code === 'auth/invalid-action-code') {
        setError(
          'This reset link is invalid. Please request a new one.'
        );
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* LOGO */}
        <div style={styles.logoRow}>
          <div style={styles.logoMark}>
            <img
              src={logo}
              alt="Iyanna Foundation"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
          <div>
            <div style={styles.logoName}>
              Iyanna Foundation
            </div>
            <div style={styles.logoSub}>
              PROTECT & EDUCATE
            </div>
          </div>
        </div>

        <div style={styles.heading}>Reset Password</div>
        <div style={styles.sub}>
          Enter your new password below
        </div>

        {success ? (
          <div style={styles.successBox}>
            <div style={styles.successIcon}></div>
            <div style={styles.successText}>{success}</div>
            <button
              style={styles.btn}
              onClick={() => navigate('/')}
            >
              Go to Login →
            </button>
          </div>
        ) : (
          <form onSubmit={handleReset}>

            <div style={styles.label}>New Password *</div>
            <div style={styles.passwordRow}>
              <input
                style={styles.passwordInput}
                type={showPassword ? 'text' : 'password'}
                placeholder="Min 8 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                style={styles.eyeBtn}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword
                  ? <AiOutlineEyeInvisible
                      size={20}
                      color="rgba(255,255,255,0.6)"
                    />
                  : <AiOutlineEye
                      size={20}
                      color="rgba(255,255,255,0.6)"
                    />
                }
              </button>
            </div>

            {/* PASSWORD RULES */}
            {password.length > 0 && (
              <div style={styles.rules}>
                {[
                  {
                    met: password.length >= 8,
                    text: 'At least 8 characters'
                  },
                  {
                    met: !!password.match(/[A-Z]/),
                    text: 'One capital letter'
                  },
                  {
                    met: !!password.match(/[0-9]/),
                    text: 'One number'
                  },
                ].map((r, i) => (
                  <div key={i} style={{
                    ...styles.rule,
                    color: r.met
                      ? '#BBD8C4'
                      : 'rgba(255,255,255,0.3)'
                  }}>
                    {r.met ? '' : '○'} {r.text}
                  </div>
                ))}
              </div>
            )}

            <div style={styles.label}>
              Confirm New Password *
            </div>
            <div style={styles.passwordRow}>
              <input
                style={styles.passwordInput}
                type={showConfirm ? 'text' : 'password'}
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                style={styles.eyeBtn}
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm
                  ? <AiOutlineEyeInvisible
                      size={20}
                      color="rgba(255,255,255,0.6)"
                    />
                  : <AiOutlineEye
                      size={20}
                      color="rgba(255,255,255,0.6)"
                    />
                }
              </button>
            </div>

            {error && (
              <div style={styles.error}>{error}</div>
            )}

            <button
              type="submit"
              style={styles.btn}
              disabled={loading}
            >
              {loading ? 'Resetting...' : '🔐 Reset Password'}
            </button>

            <div
              style={styles.backLink}
              onClick={() => navigate('/')}
            >
              ← Back to Login
            </div>

          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(160deg, #041F14 0%, #07271A 40%, #14452F 70%, #041F14 100%)',
    padding: '40px 20px',
  },
  card: {
    background: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(24px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '24px',
    padding: '44px 40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '30px',
  },
  logoMark: {
    width: '65px',
    height: '65px',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#041F14',
    padding: '0px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
    border: '2px solid rgba(255,255,255,0.15)',
  },
  logoName: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '19px',
    color: '#fff',
    lineHeight: 1.2,
    fontWeight: '700',
  },
  logoSub: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '1px',
    marginTop: '2px',
  },
  heading: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '27px',
    color: '#fff',
    marginBottom: '5px',
  },
  sub: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.45)',
    marginBottom: '28px',
  },
  label: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: '7px',
  },
  passwordRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    width: '100%',
  },
  passwordInput: {
    width: '100%',
    background: 'rgba(255,255,255,0.07)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    padding: '13px 48px 13px 16px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  eyeBtn: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rules: {
    marginBottom: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  rule: {
    fontSize: '11.5px',
    transition: 'color 0.2s',
  },
  error: {
    background: 'rgba(211,47,47,0.2)',
    border: '1px solid rgba(211,47,47,0.4)',
    color: '#ff8a80',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '14px',
  },
  btn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: '0 8px 22px rgba(245,124,0,0.3)',
    marginBottom: '16px',
  },
  backLink: {
    textAlign: 'center',
    color: 'rgba(255,255,255,0.4)',
    fontSize: '13px',
    cursor: 'pointer',
  },
  successBox: {
    textAlign: 'center',
    padding: '20px 0',
  },
  successIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  successText: {
    color: '#BBD8C4',
    fontSize: '14px',
    lineHeight: 1.6,
    marginBottom: '24px',
  },
};